const STOPWORDS = new Set([
  'a',
  'an',
  'the',
  'and',
  'or',
  'of',
  'in',
  'on',
  'for',
  'with',
  'at',
  'to',
  'by',
  'is',
  'are',
  'was',
  'were',
  'be',
  'been',
  'this',
  'that',
  'these',
  'those',
  'it',
  'its',
  'as',
  'from',
  'how',
  'what',
  'when',
  'where',
  'which',
  'who',
  'why',
  'your',
  'their',
  'our',
  'his',
  'her',
  'my',
  'me',
  'we',
  'you',
  'they',
  'he',
  'she',
  'but',
  'so',
  'not',
  'no',
  'yes',
  'can',
  'will',
  'would',
  'could',
  'should',
  'may',
  'might',
  'must',
  'do',
  'does',
  'did',
  'about',
  'into',
  'over',
  'under',
]);

export const escapeRegExp = str => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function getDomain(url) {
  if (!url) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

function decodeEntities(text) {
  return String(text)
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&nbsp;/g, ' ');
}

export function cleanText(text) {
  if (!text) return '';
  let str = String(text);
  str = decodeEntities(str);
  str = str.replace(/<[^>]*>/g, ' ');
  str = str.replace(/\s+/g, ' ').trim();
  return str;
}

function prepareSnippet(text) {
  if (!text) return '';
  let str = String(text);
  str = decodeEntities(str);
  str = str.replace(/<[^>]*>/g, ' ');
  str = str.replace(/[ \t]+/g, ' ');
  str = str.replace(/\n{3,}/g, '\n\n');
  return str.trim();
}

export function getFaviconUrl(domain) {
  if (!domain) return '';
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=128`;
}

function getDomainStem(domain) {
  if (!domain) return '';
  const parts = domain.split('.');
  return parts.length >= 2 ? parts.slice(0, -1).join('.') : domain;
}

function cleanToken(token) {
  return token.replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, '');
}

function getEntityName(title) {
  if (!title) return '';
  const words = title.split(/\s+/).filter(Boolean);

  let index = 0;
  while (index < words.length && STOPWORDS.has(words[index].toLowerCase())) index++;
  if (index >= words.length) return '';

  const first = cleanToken(words[index]);
  if (first.length < 3) return '';

  const name = [first];
  const next = words[index + 1] ? cleanToken(words[index + 1]) : '';
  const isCapitalized = word => /^[A-Z]/.test(word);
  if (next && next.length >= 3 && isCapitalized(next) && !STOPWORDS.has(next.toLowerCase())) {
    name.push(next);
  }

  return name.join(' ').trim();
}

function getProperNouns(title) {
  if (!title) return [];
  const words = title.split(/\s+/).map(cleanToken).filter(Boolean);

  const nouns = [];
  const seen = new Set();
  for (const word of words) {
    if (word.length < 3) continue;
    const lower = word.toLowerCase();
    if (STOPWORDS.has(lower)) continue;
    if (!/^[A-Z]/.test(word)) continue;
    if (/^[0-9]/.test(word)) continue;
    if (seen.has(lower)) continue;
    seen.add(lower);
    nouns.push(word);
  }
  return nouns;
}

export function normalizeSource(source, index, images = []) {
  const url = source?.url || source?.link || source?.origin_url || '';
  const domain = getDomain(url);

  const ownImage =
    source?.image_url ||
    source?.thumbnail ||
    source?.thumbnail_url ||
    source?.thumb ||
    source?.image ||
    source?.img ||
    source?.og_image ||
    source?.['og:image'] ||
    source?.icon ||
    source?.logo ||
    source?.favicon ||
    source?.favicon_url ||
    null;

  let image = ownImage;
  if (!image && domain) {
    const matched = images?.find(item => {
      const origin = item?.origin_url || item?.url || '';
      return origin === url || getDomain(origin) === domain;
    });
    if (matched?.image_url) image = matched.image_url;
  }
  if (!image && domain) {
    image = getFaviconUrl(domain);
  }

  return {
    id: String(source?.id ?? index),
    number: Number(index) + 1,
    title: cleanText(source?.title || source?.name || ''),
    url,
    domain,
    snippet: prepareSnippet(source?.snippet || source?.description || source?.content || source?.summary || ''),
    image,
  };
}

export function getSourceTerms(source) {
  const terms = [];
  const entity = getEntityName(source.title);
  if (entity) terms.push({ text: entity, priority: 3 });

  const properNouns = getProperNouns(source.title).slice(0, 4);
  for (const noun of properNouns) {
    if (noun.toLowerCase() === entity.toLowerCase()) continue;
    terms.push({ text: noun, priority: 2 });
  }

  const stem = getDomainStem(source.domain);
  if (stem && stem.length >= 3) terms.push({ text: stem, priority: 2 });
  if (source.domain && source.domain.length >= 4) terms.push({ text: source.domain, priority: 1 });

  return terms;
}

export function getProtectedRanges(text) {
  const ranges = [];

  let match;
  const markRe = /<mark\b[^>]*>[\s\S]*?<\/mark>/g;
  while ((match = markRe.exec(text)) !== null) {
    ranges.push([match.index, match.index + match[0].length]);
  }

  const fenceRe = /```[\s\S]*?```/g;
  while ((match = fenceRe.exec(text)) !== null) {
    ranges.push([match.index, match.index + match[0].length]);
  }

  const codeRe = /`[^`\n]*`/g;
  while ((match = codeRe.exec(text)) !== null) {
    ranges.push([match.index, match.index + match[0].length]);
  }

  const linkRe = /\[[^\]]*\]\([^)]*\)/g;
  while ((match = linkRe.exec(text)) !== null) {
    ranges.push([match.index, match.index + match[0].length]);
  }

  const urlRe = /(?:https?:\/\/|www\.)[^\s"'<>)]+/g;
  while ((match = urlRe.exec(text)) !== null) {
    ranges.push([match.index, match.index + match[0].length]);
  }

  return ranges;
}

export function isProtected(start, end, ranges) {
  return ranges.some(([rangeStart, rangeEnd]) => start < rangeEnd && end > rangeStart);
}

export function buildHighlightedMarkdown(text, sources, images = []) {
  if (!text || !Array.isArray(sources) || !sources.length) {
    return { markdown: text, citeById: {} };
  }

  const normalized = sources.map((source, index) => normalizeSource(source, index, images));
  const citeById = {};
  const terms = [];

  for (const source of normalized) {
    citeById[source.id] = source;
    for (const term of getSourceTerms(source)) {
      if (!term.text) continue;
      if (terms.some(existing => existing.text.toLowerCase() === term.text.toLowerCase())) continue;
      terms.push({ text: term.text, sourceId: source.id, priority: term.priority });
    }
  }

  if (!terms.length) return { markdown: text, citeById };

  terms.sort((a, b) => b.priority - a.priority || b.text.length - a.text.length);

  const protectedRanges = getProtectedRanges(text);
  const used = [];
  const matches = [];
  const sourceCount = {};
  const MAX_PER_SOURCE = 4;

  for (const term of terms) {
    const regex = new RegExp(escapeRegExp(term.text), 'gi');
    let match;
    while ((match = regex.exec(text)) !== null) {
      if ((sourceCount[term.sourceId] || 0) >= MAX_PER_SOURCE) break;
      const start = match.index;
      const end = start + match[0].length;
      if (isProtected(start, end, protectedRanges)) continue;
      if (used.some(([usedStart, usedEnd]) => start < usedEnd && end > usedStart)) continue;
      used.push([start, end]);
      matches.push({ start, end, sourceId: term.sourceId, text: match[0] });
      sourceCount[term.sourceId] = (sourceCount[term.sourceId] || 0) + 1;
    }
  }

  if (!matches.length) return { markdown: text, citeById };

  matches.sort((a, b) => a.start - b.start);

  let markdown = '';
  let cursor = 0;
  for (const match of matches) {
    markdown += text.slice(cursor, match.start);
    markdown += `<mark data-cite="${match.sourceId}">${match.text}</mark>`;
    cursor = match.end;
  }
  markdown += text.slice(cursor);

  return { markdown, citeById };
}
